import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, ActivityIndicator, Platform, Image, Pressable, Linking, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import { supabase } from '@/data/client';
import { getPublicForm, submitForm } from '@/data/forms';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import FormView from '@/components/Forms/FormView';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';

const REACH_LOGO_LIGHT = require('@/assets/logo/Azul.png');
const REACH_LOGO_DARK = require('@/assets/logo/GhostWhite.png');
const REACH_WEBSITE_URL = 'https://www.reachchms.com';

// Native-only back button for the error/state screens (not_found, forbidden,
// closed). Mirrors the overlay back button on the main form view so a logged-in
// user is never trapped on a state screen with no way out. Web users have
// browser/iframe navigation so this is native-only.
const StateBackButton = () => {
    const router = useRouter();
    const navigation = useNavigation();
    const insets = useSafeAreaInsets();
    if (Platform.OS === 'web') return null;
    const goBack = () => {
        if (navigation.canGoBack()) router.back();
        else router.replace('/(tabs)/(home)');
    };
    return (
        <Pressable
            style={[styles.backButton, { top: insets.top + 8 }]}
            onPress={goBack}
            testID="public-form-back"
        >
            <Unicon name="chevron-back-outline" size={24} colorVariant="text" />
        </Pressable>
    );
};

const HCAPTCHA_SITE_KEY = process.env.EXPO_PUBLIC_HCAPTCHA_SITE_KEY;
const HCAPTCHA_SCRIPT_SRC = 'https://js.hcaptcha.com/1/api.js';

/**
 * Public form renderer route — `/f/{publicId}`.
 *
 * Loaded directly in a browser (anonymous) or inside a WebView/iframe by
 * `FormEmbed` (in-app, with auth token injected into localStorage). When a
 * Supabase session is present it is passed to `getPublicForm` so role-scoped
 * forms and Name autofill work.
 */
const PublicFormPage = () => {
    const { publicId } = useLocalSearchParams();
    const router = useRouter();
    const navigation = useNavigation();
    const insets = useSafeAreaInsets();
    const { theme: { colors, scheme } } = useUnistyles();
    const queryClient = useQueryClient();

    // Keep the browser background in sync with the active theme so the
    // overscroll area doesn't show a mismatched color.
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        if (typeof document === 'undefined') return;
        document.documentElement.style.backgroundColor = colors.background;
        document.body.style.backgroundColor = colors.background;
        return () => {
            document.documentElement.style.backgroundColor = '';
            document.body.style.backgroundColor = '';
        };
    }, [colors.background]);

    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);
    const [status, setStatus] = useState(null); // 'closed' | 'not_found' | 'forbidden' | 'network' | 'server_error' | null
    const [accessToken, setAccessToken] = useState(null);
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [captchaToken, setCaptchaToken] = useState(null);
    const captchaContainerRef = useRef(null);
    const captchaWidgetIdRef = useRef(null);
    const captchaResolveRef = useRef(null);

    // Fetch the logged-in user profile when a session exists (for Name autofill).
    const loggedInUserQuery = Query.people.loggedInUser({
        options: { enabled: !!accessToken },
    });

    useEffect(() => {
        if (accessToken && loggedInUserQuery.data) {
            setLoggedInUser(loggedInUserQuery.data);
        }
    }, [accessToken, loggedInUserQuery.data]);

    // hCaptcha: inject the script. Widget is rendered after the form loads
    // (the container element only exists once FormView renders renderCaptcha).
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        if (!HCAPTCHA_SITE_KEY) return;

        if (!document.querySelector(`script[src="${HCAPTCHA_SCRIPT_SRC}"]`)) {
            const script = document.createElement('script');
            script.src = HCAPTCHA_SCRIPT_SRC;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }
    }, []);

    // Render the invisible hCaptcha widget once the form has loaded and the
    // container element is present in the DOM.
    useEffect(() => {
        if (Platform.OS !== 'web') return;
        if (!HCAPTCHA_SITE_KEY) return;
        if (!form) return;

        const renderWidget = () => {
            const el = captchaContainerRef.current;
            if (!el || el.querySelector('.h-captcha')) return;
            if (!window.hcaptcha) return;

            const widget = document.createElement('div');
            el.appendChild(widget);
            try {
                const id = window.hcaptcha.render(widget, {
                    sitekey: HCAPTCHA_SITE_KEY,
                    size: 'invisible',
                    callback: (token) => {
                        setCaptchaToken(token);
                        if (captchaResolveRef.current) {
                            captchaResolveRef.current(token);
                            captchaResolveRef.current = null;
                        }
                    },
                    'error-callback': () => {
                        if (captchaResolveRef.current) {
                            captchaResolveRef.current(null);
                            captchaResolveRef.current = null;
                        }
                    },
                });
                if (id != null) captchaWidgetIdRef.current = id;
            } catch { /* noop */ }
        };

        // Poll until hCaptcha script + container are both ready.
        let attempts = 0;
        const maxAttempts = 20;
        const interval = setInterval(() => {
            attempts++;
            if (window.hcaptcha && captchaContainerRef.current) {
                renderWidget();
                clearInterval(interval);
            } else if (attempts >= maxAttempts) {
                clearInterval(interval);
            }
        }, 200);

        return () => clearInterval(interval);
    }, [form]);

    // Load the form on mount — also re-invoked on a 409 form_changed so the
    // respondent gets the current schema/revision. `spinner` stays false on
    // mid-fill refreshes (FormView keeps the screen and ordinary answers).
    const loadForm = useCallback(async ({ spinner = true, cancelled = { current: false } } = {}) => {
        if (spinner) setLoading(true);
        try {
            // Check for an existing Supabase session (same-origin web or
            // injected by a native WebView via localStorage).
            let token = null;
            try {
                const { data: sessionData } = await supabase.auth.getSession();
                token = sessionData?.session?.access_token || null;
            } catch { /* anonymous — no session */ }

            if (cancelled.current) return;
            setAccessToken(token);

            const result = await getPublicForm({ publicId, accessToken: token });

            if (cancelled.current) return;

            if (result?.closed) {
                setForm(result.form || {});
                setStatus('closed');
            } else if (result?.error === 'not_found') {
                setStatus('not_found');
            } else if (result?.error === 'forbidden') {
                setStatus('forbidden');
            } else if (result?.form) {
                setForm(result.form);
                setStatus(null);
            } else {
                setStatus('not_found');
            }
        } catch (err) {
            if (!cancelled.current) {
                // Distinguish network/server failures from a genuinely missing
                // form so the user sees the right message. See CHURCH-APP-13M.
                const msg = err?.message || '';
                if (msg === 'network') setStatus('network');
                else if (msg === 'server_error') setStatus('server_error');
                else setStatus('not_found');
            }
        } finally {
            if (!cancelled.current && spinner) setLoading(false);
        }
    }, [publicId]);

    useEffect(() => {
        if (!publicId) return undefined;
        const cancelled = { current: false };
        loadForm({ cancelled });
        return () => { cancelled.current = true; };
    }, [publicId, loadForm]);

    // Execute invisible hCaptcha and return a promise that resolves with the token.
    // Always executes fresh — hCaptcha tokens are single-use.
    const executeCaptcha = useCallback(() => {
        return new Promise((resolve) => {
            if (!window.hcaptcha || captchaWidgetIdRef.current == null) {
                // hCaptcha not loaded — proceed without (server will reject if required).
                resolve(null);
                return;
            }
            captchaResolveRef.current = resolve;
            try {
                // Reset any previous state, then execute for a fresh token.
                window.hcaptcha.reset(captchaWidgetIdRef.current);
                window.hcaptcha.execute(captchaWidgetIdRef.current);
            } catch {
                captchaResolveRef.current = null;
                resolve(null);
                return;
            }
            // Timeout: don't hang forever if the challenge never completes.
            setTimeout(() => {
                if (captchaResolveRef.current) {
                    captchaResolveRef.current(null);
                    captchaResolveRef.current = null;
                }
            }, 60000);
        });
    }, []);

    const handleSubmit = useCallback(async (payload, files) => {
        // Pull spam-protection metadata that FormView attaches under `_meta`.
        const meta = payload?._meta || {};
        const website = meta.honeypot || '';
        const formLoadedAt = meta.formLoadedAt;

        // Honeypot tripped — bot filled the hidden field. Pretend success
        // without calling the edge function (saves an invocation).
        if (website.trim() !== '') {
            return {
                successMessage: form?.successMessage || 'Thanks! Your response has been recorded.',
                redirectUrl: null,
            };
        }

        // Execute invisible hCaptcha for a fresh single-use token.
        let token = null;
        if (Platform.OS === 'web' && HCAPTCHA_SITE_KEY) {
            token = await executeCaptcha();
        }

        try {
            const result = await submitForm({
                publicId,
                submissionID: meta.submissionID,
                // Server digest echoed back — a stale revision is a 409
                // `form_changed` when the live schema has signature fields.
                contentRevision: meta.contentRevision ?? form?.contentRevision ?? null,
                payload,
                files,
                accessToken,
                captcha_token: token,
                website,
                form_loaded_at: formLoadedAt,
            });

            if (result?.errors) {
                // Surface server field errors via Toast; FormView maps the
                // structured keys (fieldId / repeat instance key) back onto
                // fields via err.fieldErrors.
                const firstError = Object.values(result.errors)[0];
                Toast.show({ type: 'error', text2: firstError || 'Please fix the highlighted fields.' });
                const err = new Error(firstError || 'Validation failed');
                err.fieldErrors = result.errors;
                throw err;
            }

            if (result?.error) {
                Toast.show({ type: 'error', text2: result.error });
                throw new Error(result.error);
            }

            if (result?.success) {
                // Notify an enclosing iframe/WebView that the form was submitted.
                if (result.payment?.snapshot?.channel !== 'electronic' && Platform.OS === 'web' && typeof window !== 'undefined' && window.parent !== window) {
                    window.parent.postMessage({
                        type: 'FORM_SUBMITTED',
                        entryId: result.entryId,
                    }, '*');
                }
                if (form?.id) {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.formEntries.byFormId(form.id) }).catch(() => {});
                }
                return {
                    successMessage: result.successMessage,
                    payment: result.payment,
                    entryId: result.entryId,
                    redirectUrl: result.redirectUrl,
                };
            }

            return result;
        } finally {
            // Clear the cached token so the next submit gets a fresh one.
            setCaptchaToken(null);
        }
    }, [publicId, accessToken, executeCaptcha, form, queryClient]);

    const handleSuccess = useCallback((result) => {
        if (result?.payment?.snapshot?.channel === 'electronic' && ['PAID', 'PENDING'].includes(result.payment.status) && Platform.OS === 'web') {
            const message = { type: 'FORM_SUBMITTED', entryId: result.entryId };
            if (window.parent !== window) window.parent.postMessage(message, '*');
            window.ReactNativeWebView?.postMessage(JSON.stringify(message));
        }
        // FormView handles the success Callout + redirect internally; nothing
        // extra to do here beyond the postMessage (done in handleSubmit).
    }, []);

    const renderCaptcha = useCallback(() => {
        if (Platform.OS !== 'web') return null;
        if (!HCAPTCHA_SITE_KEY) {
            return null;
        }
        // Invisible hCaptcha: container is hidden, widget executes on submit.
        return <View ref={captchaContainerRef} style={styles.captchaHidden} />;
    }, []);

    if (loading) {
        return (
            <View style={styles.stateContainer} testID="public-form-page">
                <ActivityIndicator size="large" color={colors.forms} />
            </View>
        );
    }

    if (status === 'not_found') {
        return (
            <View style={styles.stateContainer} testID="public-form-page">
                <StateBackButton />
                <View style={styles.stateCard} testID="form-not-found">
                    <Unicon name="document-text-outline" size={48} colorVariant="forms" />
                    <Text style={styles.stateTitle}>Form not found</Text>
                    <Text style={styles.stateMessage}>
                        The form you&apos;re looking for may have been removed or is no longer available.
                    </Text>
                </View>
            </View>
        );
    }

    if (status === 'forbidden') {
        return (
            <View style={styles.stateContainer} testID="public-form-page">
                <StateBackButton />
                <View style={styles.stateCard} testID="form-forbidden">
                    <Unicon name="lock-closed" size={48} colorVariant="forms" />
                    <Text style={styles.stateTitle}>Access restricted</Text>
                    <Text style={styles.stateMessage}>
                        You don&apos;t have access to this form. Please contact your organization if you believe this is an error.
                    </Text>
                </View>
            </View>
        );
    }

    if (status === 'closed') {
        return (
            <View style={styles.stateContainer} testID="public-form-page">
                <StateBackButton />
                <View style={styles.stateCard} testID="form-closed">
                    <Unicon name="lock-closed" size={48} colorVariant="forms" />
                    {!!form?.title && <Text style={styles.stateTitle}>{form.title}</Text>}
                    <Text style={styles.stateMessage}>
                        This form is no longer accepting responses.
                    </Text>
                </View>
            </View>
        );
    }

    if (status === 'network' || status === 'server_error') {
        return (
            <View style={styles.stateContainer} testID="public-form-page">
                <StateBackButton />
                <View style={styles.stateCard} testID={`form-${status}`}>
                    <Unicon name="cloud-offline-outline" size={48} colorVariant="forms" />
                    <Text style={styles.stateTitle}>Couldn&apos;t load the form</Text>
                    <Text style={styles.stateMessage}>
                        We couldn&apos;t reach the server to load this form. Please check your connection and try again.
                    </Text>
                </View>
            </View>
        );
    }

    if (!form) {
        return (
            <View style={styles.stateContainer} testID="public-form-page">
                <StateBackButton />
                <View style={styles.stateCard} testID="form-not-found">
                    <Unicon name="document-text-outline" size={48} colorVariant="forms" />
                    <Text style={styles.stateTitle}>Form not found</Text>
                    <Text style={styles.stateMessage}>
                        The form you&apos;re looking for may have been removed or is no longer available.
                    </Text>
                </View>
            </View>
        );
    }

    const orgLogoLong = scheme === 'light'
        ? form?.organization?.logoLongDark
        : form?.organization?.logoLong;
    const orgLogoSquare = scheme === 'light'
        ? form?.organization?.logoDark
        : form?.organization?.logo;
    const orgLogo = orgLogoLong || orgLogoSquare;

    return (
        <View style={styles.page} testID="public-form-page">
            {/* Back button (top-left) — fixed overlay, stays put while content scrolls */}
            {Platform.OS !== 'web' && (
                <Pressable
                    style={[styles.backButton, { top: insets.top + 8 }]}
                    onPress={() => {
                        if (navigation.canGoBack()) router.back();
                        else router.replace('/(tabs)/(home)');
                    }}
                    testID="public-form-back"
                >
                    <Unicon name="chevron-back-outline" size={24} colorVariant="text" />
                </Pressable>
            )}

            {/* Logged-in user badge (top-right) — fixed overlay */}
            {loggedInUser && (
                <View style={[styles.userBadge, { top: insets.top + 8 }]} testID="public-form-user-badge">
                    <Avatar profile={loggedInUser} size={28} disableBorder />
                    <Text style={styles.userBadgeName} numberOfLines={1}>
                        {loggedInUser.firstName || ''} {loggedInUser.lastName || ''}
                    </Text>
                </View>
            )}

            {/* Single scroll container: org header, form, and footer scroll together */}
            <ScrollView
                style={styles.pageScroll}
                contentContainerStyle={[styles.pageScrollContent, { paddingBottom: insets.bottom }]}
                keyboardShouldPersistTaps="handled"
            >
                <View style={[styles.pageInner, { paddingTop: Platform.OS === 'web' ? undefined : insets.top + 44 }]}>
                    {/* Org branding header */}
                    {(orgLogo || form?.organization?.name) && (
                        <View style={styles.orgHeader}>
                            {orgLogo ? (
                                <Image
                                    source={{ uri: orgLogo }}
                                    style={orgLogoLong ? styles.orgLogo : styles.orgLogoSquare}
                                    resizeMode="contain"
                                />
                            ) : (
                                <Text style={styles.orgName}>{form.organization.name}</Text>
                            )}
                        </View>
                    )}

                    <FormView
                        form={form}
                        isPublic={!accessToken}
                        // Resource URLs on public fills resolve anonymously by
                        // opaque id — the load response never echoes it back.
                        publicId={publicId}
                        loggedInUser={loggedInUser}
                        captchaToken={captchaToken}
                        renderCaptcha={renderCaptcha}
                        onSubmit={handleSubmit}
                        onSuccess={handleSuccess}
                        // 409 form_changed — reload silently; FormView has
                        // already stripped signature/consent answers.
                        onFormChanged={() => loadForm({ spinner: false })}
                        scrollable={false}
                    />

                    {/* Powered by Reach footer */}
                    <Pressable
                        style={styles.poweredBy}
                        onPress={() => Linking.openURL(REACH_WEBSITE_URL)}
                    >
                        <Text style={styles.poweredByText}>Powered by</Text>
                        <Image
                            source={scheme === 'light' ? REACH_LOGO_LIGHT : REACH_LOGO_DARK}
                            style={styles.reachLogo}
                            resizeMode="contain"
                        />
                    </Pressable>
                </View>
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, spacing, typography, fonts, shadows, roundness }) => ({
    page: {
        flex: 1,
        position: 'relative',
        backgroundColor: colors.background,
        _web: {
            minHeight: '100vh',
        },
    },
    pageScroll: {
        flex: 1,
        width: '100%',
    },
    pageScrollContent: {
        flexGrow: 1,
        alignItems: 'center',
    },
    userBadge: {
        position: 'absolute',
        top: spacing.sm,
        right: spacing.md,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs,
        zIndex: 10,
    },
    userBadgeName: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        maxWidth: 150,
    },
    backButton: {
        position: 'absolute',
        left: spacing.md,
        zIndex: 10,
        padding: spacing.xs,
    },
    pageInner: {
        width: '100%',
        maxWidth: 600,
        paddingVertical: spacing.lg,
        flex: 1,
    },
    orgHeader: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: spacing.md,
        marginBottom: spacing.sm,
    },
    orgLogo: {
        width: 200,
        height: 80,
        maxHeight: 80,
    },
    orgLogoSquare: {
        width: 64,
        height: 64,
        borderRadius: roundness.md,
    },
    orgName: {
        fontSize: typography.h2,
        fontFamily: fonts.bold,
        color: colors.text,
        textAlign: 'center',
    },
    poweredBy: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: spacing.md,
        marginTop: spacing.sm,
        _web: {
            cursor: 'pointer',
            transition: 'opacity 0.15s ease',
            _hover: { opacity: 0.7 },
        },
    },
    poweredByText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textTertiary,
    },
    reachLogo: {
        width: 80,
        height: 21,
    },
    stateContainer: {
        flex: 1,
        position: 'relative',
        backgroundColor: colors.background,
        justifyContent: 'center',
        alignItems: 'center',
        padding: spacing.lg,
    },
    stateCard: {
        alignItems: 'center',
        gap: spacing.sm,
        maxWidth: 420,
        backgroundColor: colors.surface,
        borderRadius: roundness.lg,
        padding: spacing.xl,
        boxShadow: shadows.card,
    },
    stateTitle: {
        fontSize: typography.h2,
        fontFamily: fonts.bold,
        color: colors.text,
        textAlign: 'center',
        marginTop: spacing.xs,
    },
    stateMessage: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    captchaHidden: {
        position: 'absolute',
        left: -9999,
        top: -9999,
        width: 1,
        height: 1,
        overflow: 'hidden',
        opacity: 0,
    },
}));

export default PublicFormPage;
